import { Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { ErrorState } from '@/components/ErrorState'
import { Button } from '@/components/ui/button'
import { PageContainer, PageHeader } from '@/layout/PageHeader'
import { AdminHome } from './home/AdminHome'
import { DeveloperHome } from './home/DeveloperHome'
import { MaintainerHome } from './home/MaintainerHome'
import { QaHome } from './home/QaHome'
import { RadiologistHome } from './home/RadiologistHome'
import { TechnicianHome } from './home/TechnicianHome'
import { useTaskBuckets } from './home/useTaskBuckets'
import type { Role } from '@/api/types'

/** Where this role's actual work lives. Not `getDefaultLanding` — that returns
 *  `/` for admin and maintainer, which is this page. */
const PRIMARY_ACTION: Record<Role, { to: string; label: string }> = {
  admin: { to: '/work?tab=decide', label: 'Needs decision' },
  maintainer: { to: '/work?tab=decide', label: 'Needs decision' },
  radiologist: { to: '/work?tab=decide', label: 'Needs decision' },
  technician: { to: '/work?tab=available', label: 'Available work' },
  qa: { to: '/work?tab=available', label: 'Available samples' },
  developer: { to: '/data', label: 'Query data' },
}

const DESCRIPTION: Record<string, string> = {
  admin: 'Everything blocked on a decision, how the pipeline is flowing, and who is doing what.',
  maintainer: 'What is blocked on you, how the pipeline is flowing, and what just landed.',
  radiologist: 'Reads waiting on your verdict, and the ones you can pick up.',
  technician: 'Pick up where you left off, then what else you can claim.',
  developer: 'What is in the index, and how to get it out.',
  qa: 'Your QA samples, plus the overrides and quarantines worth a second look.',
}

/**
 * `/` — role-shaped home. Replaces `DashboardPage`, which showed four
 * non-clickable counts computed from *global* task state: a technician's
 * "In progress" counted other people's work, and radiologist/developer/qa landed
 * on an empty quick-actions card.
 *
 * Three rules hold across every section (see `home/shared.tsx`):
 *
 * 1. **Every number is a link, and its destination arrives pre-filtered.**
 *    A tile reading "3 need rework" goes to `/work?tab=progress`, never `/work`.
 * 2. **Counts labelled "my" are scoped to me**, via `isAssignedToUser` in
 *    `home/useTaskBuckets.ts` — the bug the old dashboard had.
 * 3. **Every query is gated on the permission its endpoint requires.** A query a
 *    role would 403 on isn't harmless: `App.tsx`'s `QueryCache.onError` raises a
 *    destructive toast for anything carrying a request id. A technician fires
 *    only `['tasks']`; a developer never fires it at all (the role has no
 *    `task:read`).
 */
export function HomePage() {
  const { me } = useAuth()
  // Sections read `me` unconditionally; `RequireRole` has already resolved it,
  // but a null slips through for one frame on a hard reload.
  if (!me) return null

  return (
    <PageContainer>
      <PageHeader
        title={greeting(me.email)}
        description={DESCRIPTION[me.role] ?? 'Your work, and what needs a decision.'}
        actions={
          <Button asChild variant="outline">
            <Link to={PRIMARY_ACTION[me.role].to}>{PRIMARY_ACTION[me.role].label}</Link>
          </Button>
        }
      />
      <div className="space-y-8">
        <RoleSections />
      </div>
    </PageContainer>
  )
}

/**
 * The one branch on role. Split out so the hook runs below the `!me` guard and
 * each role's sections stay independently readable — a section per concern, not
 * one page of conditionals.
 */
function RoleSections() {
  const { me } = useAuth()
  const buckets = useTaskBuckets()

  if (buckets.isError) {
    return (
      <ErrorState message="Couldn't load your tasks. Home is read-only, so nothing is lost — reload to try again." />
    )
  }

  switch (me?.role) {
    case 'admin':
      return <AdminHome buckets={buckets} />
    case 'maintainer':
      return <MaintainerHome buckets={buckets} />
    case 'radiologist':
      return <RadiologistHome buckets={buckets} />
    case 'qa':
      return <QaHome buckets={buckets} />
    case 'developer':
      return <DeveloperHome />
    default:
      return <TechnicianHome buckets={buckets} />
  }
}

function greeting(email: string | null): string {
  const name = email?.split('@')[0]
  return name ? `Welcome back, ${name}` : 'Welcome back'
}
